/**
 * 계약서 본문 마커 파싱 유틸.
 *
 * 마커 표기법 (ContractEditor 의 jsonToPlainMarker 와 일치):
 *  - `[variableName]`            — 변수 (자동/수동 변수, 영문 키)
 *  - `{{변수}}`                   — 레거시 (한글 키도 가능, LEGACY_KEY_MAP 으로 영문 키로 정규화)
 *  - `[[필드:type|key|label]]`    — 인터랙티브 필드 (signature/checkbox/text/date/seal)
 *  - `[[PAGE:N]]`                — 페이지 구분자 (인라인 렌더에서는 무시 — 줄바꿈으로 대체)
 *
 * FE 인라인 렌더러 (InlineContractRenderer) 와 ContractRequestView 양쪽에서 공용.
 */

/** 토큰 종류 */
export type TokenKind =
  | "text"        // 일반 본문 텍스트
  | "filled"      // 변수 — 서버 prefilled 값 있음 → 읽기전용 인라인 텍스트
  | "varEmpty"    // 변수 — 값 없음 → 빈 인풋 (사장님이 직접 만든 수동 변수 등)
  | "addr"        // 주소 — 필드(address) 또는 [guardianAddress] 변수 모두 매핑
  | "signature"   // 필드 signature → 서명 캔버스
  | "checkbox"    // 필드 checkbox → 동의 체크박스
  | "text-field"  // 필드 text → 텍스트 인풋
  | "date-field"  // 필드 date → 날짜 인풋
  | "seal";       // 필드 seal → 도장 이미지 (보호자 측은 읽기전용)

export interface Token {
  kind: TokenKind;
  /** text → 본문 내용 / 그 외 → 변수 또는 필드 key */
  value: string;
  /** filled 일 때 prefilled 텍스트 / 필드 토큰의 label */
  filledText?: string;
  /** 필드 토큰의 라벨 */
  label?: string;
  /** 필드 타입 (signature/seal 등) - 디버그/조건 분기용 */
  fieldType?: string;
}

/** 레거시 한글 변수 키 → 영문 키 매핑 (ContractEditor 와 동기화) */
const LEGACY_KEY_MAP: Record<string, string> = {
  보호자명: "guardianName",
  보호자: "guardianName",
  보호자이름: "guardianName",
  닉네임: "guardianNickname",
  이메일: "guardianEmail",
  전화번호: "guardianPhone",
  보호자연락처: "guardianPhone",
  주소: "guardianAddress",
  펫이름: "petName",
  반려동물명: "petName",
  반려동물이름: "petName",
  품종: "petBreed",
  견종: "petBreed",
  체중: "petWeight",
  성별: "petNeutered",
  나이: "petBirthday",
  생년월일: "petBirthday",
  특이사항: "petOther",
  알러지: "petAllergies",
  알레르기: "petAllergies",
  접종: "petVaccinations",
  사업장명: "companyName",
  사업장이름: "companyName",
  상호: "companyName",
  대표자명: "companyName",
  사업자번호: "businessNumber",
  계약일: "contractDate",
  작성일: "contractDate",
};

const VARIABLE_LABELS: Record<string, string> = {
  guardianName: "보호자 이름",
  guardianNickname: "보호자 닉네임",
  guardianEmail: "보호자 이메일",
  guardianPhone: "보호자 연락처",
  guardianAddress: "보호자 주소",
  petName: "반려동물 이름",
  petBirthday: "생년월일",
  petBreed: "품종",
  petWeight: "몸무게",
  petNeutered: "중성화 여부",
  petVaccinations: "접종 정보",
  petPersonality: "성격/성향",
  petAllergies: "알레르기",
  petOther: "기타 특이사항",
  companyName: "사업장명",
  businessNumber: "사업자번호",
  contractDate: "작성일",
  startDate: "시작일",
  endDate: "종료일",
  fee: "위탁료",
};

export function variableDisplayLabel(key: string | null | undefined): string {
  const raw = (key ?? "").trim();
  if (!raw) return "입력";
  const mapped = variableKeyFromDisplay(raw);
  return VARIABLE_LABELS[mapped] ?? raw;
}

export function variableKeyFromDisplay(key: string | null | undefined): string {
  const raw = (key ?? "").trim();
  if (!raw) return "";
  const compact = raw.replace(/\s+/g, "");
  return LEGACY_KEY_MAP[raw] ?? LEGACY_KEY_MAP[compact] ?? raw;
}

/** 주소 변수 키 — 인라인에서 AddressSearch 노출 */
const ADDRESS_KEYS = new Set(["guardianAddress", "address", "addr"]);

/** 마커 매칭 정규식 */
// [[필드:type|key|label]] — 라벨 옵션
const FIELD_RE = /\[\[필드:([^|]+)\|([^|\]]+)(?:\|([^\]]+))?\]\]/g;
// [variableName] — 단일 대괄호 (camelCase)
const VAR_RE = /\[([a-zA-Z][a-zA-Z0-9_]*)\]/g;
// {{변수}} — 레거시 (한글 키 포함)
const LEGACY_VAR_RE = /\{\{\s*([^}]+?)\s*\}\}/g;
// [[PAGE:N]] — 페이지 구분자
const PAGE_RE = /\[\[PAGE:(\d+)\]\]/g;

interface RawMatch {
  start: number;
  end: number;
  kind:
    | "field"
    | "var"
    | "legacyVar"
    | "page";
  raw: string;
  // 필드용
  fieldType?: string;
  fieldKey?: string;
  fieldLabel?: string;
  // 변수용
  varKey?: string;
}

/**
 * 본문(`contractContents`)을 토큰 배열로 파싱.
 * @param body 마커 포함 본문 문자열
 * @param variables 서버 prefilled 변수 맵 (영문 키)
 */
export function parseContractBody(
  body: string,
  variables: Record<string, string | null | undefined> = {},
): Token[] {
  if (!body) return [];

  // 1) 모든 마커 위치를 한 번에 수집 → 충돌 없는 정렬된 매치로 합침
  const matches: RawMatch[] = [];
  let m: RegExpExecArray | null;

  FIELD_RE.lastIndex = 0;
  while ((m = FIELD_RE.exec(body)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      kind: "field",
      raw: m[0],
      fieldType: m[1],
      fieldKey: m[2],
      fieldLabel: m[3],
    });
  }

  PAGE_RE.lastIndex = 0;
  while ((m = PAGE_RE.exec(body)) !== null) {
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      kind: "page",
      raw: m[0],
    });
  }

  // 변수 매치는 필드/페이지 매치 범위를 피해서만 수집
  const isInsideExistingMatch = (start: number, end: number): boolean =>
    matches.some((mm) => start < mm.end && end > mm.start);

  VAR_RE.lastIndex = 0;
  while ((m = VAR_RE.exec(body)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    if (isInsideExistingMatch(start, end)) continue;
    matches.push({
      start,
      end,
      kind: "var",
      raw: m[0],
      varKey: m[1],
    });
  }

  LEGACY_VAR_RE.lastIndex = 0;
  while ((m = LEGACY_VAR_RE.exec(body)) !== null) {
    const start = m.index;
    const end = m.index + m[0].length;
    if (isInsideExistingMatch(start, end)) continue;
    const rawKey = m[1].trim();
    const mapped = LEGACY_KEY_MAP[rawKey] ?? rawKey;
    matches.push({
      start,
      end,
      kind: "legacyVar",
      raw: m[0],
      varKey: mapped,
    });
  }

  // 2) 정렬
  matches.sort((a, b) => a.start - b.start);

  // 3) 토큰 빌드
  const tokens: Token[] = [];
  let cursor = 0;
  for (const mm of matches) {
    if (mm.start > cursor) {
      tokens.push({ kind: "text", value: body.slice(cursor, mm.start) });
    }
    if (mm.kind === "page") {
      // 페이지 마커는 시각적 줄바꿈으로 대체
      tokens.push({ kind: "text", value: "\n" });
    } else if (mm.kind === "field") {
      const t = mm.fieldType!;
      const key = mm.fieldKey!;
      const label = mm.fieldLabel;
      switch (t) {
        case "signature":
          tokens.push({ kind: "signature", value: key, label, fieldType: t });
          break;
        case "checkbox":
          tokens.push({ kind: "checkbox", value: key, label, fieldType: t });
          break;
        case "text":
          tokens.push({ kind: "text-field", value: key, label, fieldType: t });
          break;
        case "date":
          tokens.push({ kind: "date-field", value: key, label, fieldType: t });
          break;
        case "seal":
          tokens.push({ kind: "seal", value: key, label, fieldType: t });
          break;
        case "address":
          tokens.push({
            kind: "addr",
            value: key,
            label,
            fieldType: t,
            filledText: variables[key] ?? undefined,
          });
          break;
        default:
          // 알 수 없는 필드 타입은 placeholder 텍스트로 표시
          tokens.push({ kind: "text", value: `[${label ?? key}]` });
      }
    } else {
      // 변수 (var | legacyVar)
      const key = mm.varKey!;
      const v = variables[key];
      if (ADDRESS_KEYS.has(key)) {
        tokens.push({ kind: "addr", value: key, filledText: v ?? undefined });
      } else if (v != null && v !== "") {
        tokens.push({ kind: "filled", value: key, filledText: v });
      } else {
        tokens.push({ kind: "varEmpty", value: key });
      }
    }
    cursor = mm.end;
  }
  if (cursor < body.length) {
    tokens.push({ kind: "text", value: body.slice(cursor) });
  }
  return tokens;
}

/** 모든 마커를 값으로 단순 치환 (상세 뷰 read-only 용 — 값 없으면 라벨 표시) */
export function substituteMarkers(
  body: string,
  variables: Record<string, string | null | undefined>,
): string {
  if (!body) return "";
  let out = body;
  // [[필드:type|key|label]] → label (값이 있으면 값, 없으면 라벨)
  out = out.replace(FIELD_RE, (_, _type: string, key: string, label?: string) => {
    const v = variables[key];
    if (v != null && v !== "") return v;
    return label ?? key;
  });
  // [variableName]
  out = out.replace(VAR_RE, (_, key: string) => {
    const v = variables[key];
    return v != null && v !== "" ? v : `[${key}]`;
  });
  // {{변수}}
  out = out.replace(LEGACY_VAR_RE, (_, raw: string) => {
    const k = raw.trim();
    const mapped = LEGACY_KEY_MAP[k] ?? k;
    const v = variables[mapped];
    return v != null && v !== "" ? v : `{{${k}}}`;
  });
  // [[PAGE:N]] → 줄바꿈
  out = out.replace(PAGE_RE, "\n");
  return out;
}
